import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type KeyedMutator } from 'swr';

import { getApiClient } from '@apps/web/lib/api_client';
import {
    type MessageRatingCreate,
    type MessageRatingLabel,
    type MessageRatingRead,
} from '@libraries/typescript/api_client';

type SingleRatingMutator = KeyedMutator<{
    rating: MessageRatingRead | null;
}>;

type RatingListMutator = KeyedMutator<{
    ratings: Array<MessageRatingRead>;
}>;

type UseMessageRatingProps = {
    readonly rating: MessageRatingRead | null;
    readonly mutate?: SingleRatingMutator;
    readonly mutateAllRatings?: RatingListMutator;
    readonly messageId: string | null;
    readonly organizationId?: string;
    readonly stagedMessageId?: string | null;
    readonly isAdmin?: boolean;
};

export function useMessageRating({
    rating,
    mutate: mutateSingle,
    mutateAllRatings: mutateRatingsList,
    messageId,
    organizationId,
    stagedMessageId,
    isAdmin = false,
}: UseMessageRatingProps) {
    // Track in-flight operations with a counter so that overlapping
    // label-changes and comment-saves do not clobber each other's
    // "in-flight" marker (the previous shared-single-ref pattern reset
    // isSaving as soon as either finished, while the other was still in
    // flight). Counter goes up at the start of each operation and down
    // in its finally block.
    const [pendingOpCount, setPendingOpCount] = useState(0);
    const beginOperation = useCallback(() => {
        setPendingOpCount((count) => count + 1);
    }, []);
    const endOperation = useCallback(() => {
        setPendingOpCount((count) => Math.max(0, count - 1));
    }, []);
    const isSaving = pendingOpCount > 0;
    // Per-handler re-entry guards. Each handler refuses to re-enter while
    // its own previous invocation is still in flight, but does not block
    // sibling handlers.
    const labelChangeInFlightRef = useRef(false);
    const commentSaveInFlightRef = useRef(false);
    const [draftComment, setDraftComment] = useState<string>('');

    const selectedLabel: MessageRatingLabel | null = rating?.label ?? null;

    // Keep draft comment in sync with server/cache when those change
    useEffect(() => {
        if (selectedLabel) {
            setDraftComment(rating?.comment ?? '');
        } else {
            setDraftComment('');
        }
    }, [selectedLabel, rating?.comment]);

    const hasUnsavedComment = useMemo(() => {
        return (
            selectedLabel !== null && draftComment !== (rating?.comment ?? '')
        );
    }, [selectedLabel, draftComment, rating?.comment]);

    const buildOptimisticRating = useCallback(
        (label: MessageRatingLabel, comment: string | null) => ({
            id: rating?.id ?? '',
            message_id: rating?.message_id ?? messageId ?? '',
            staged_message_id:
                rating?.staged_message_id ?? stagedMessageId ?? null,
            created_by_user_id: rating?.created_by_user_id ?? null,
            source: rating?.source ?? (isAdmin ? 'internal' : 'user'),
            label,
            comment,
            created_at: rating?.created_at ?? new Date().toISOString(),
            updated_at: rating?.updated_at ?? null,
        }),
        [rating, messageId, stagedMessageId, isAdmin],
    );

    const matchesTarget = useCallback(
        (candidate: MessageRatingRead) => {
            if (stagedMessageId) {
                return candidate.staged_message_id === stagedMessageId;
            }
            if (messageId) {
                return candidate.message_id === messageId;
            }
            return false;
        },
        [stagedMessageId, messageId],
    );

    const mergeRatings = useCallback(
        (
            existing: Array<MessageRatingRead> | undefined,
            next: MessageRatingRead | null,
        ): Array<MessageRatingRead> => {
            const list = existing ? [...existing] : [];
            const index = list.findIndex(matchesTarget);

            if (!next) {
                if (index === -1) {
                    return list;
                }
                list.splice(index, 1);
                return list;
            }

            if (index === -1) {
                return [...list, next];
            }

            list[index] = next;
            return list;
        },
        [matchesTarget],
    );

    const mutateRatingCache = useCallback(
        async ({
            optimistic,
            mutateFn,
        }: {
            optimistic: MessageRatingRead | null;
            mutateFn: () => Promise<MessageRatingRead | null>;
        }) => {
            if (mutateSingle) {
                await mutateSingle(
                    async () => ({
                        rating: await mutateFn(),
                    }),
                    {
                        optimisticData: { rating: optimistic },
                        rollbackOnError: true,
                        revalidate: false,
                        populateCache: true,
                    },
                );
                return;
            }

            if (mutateRatingsList) {
                await mutateRatingsList(
                    async (current) => ({
                        ratings: mergeRatings(
                            current?.ratings,
                            await mutateFn(),
                        ),
                    }),
                    {
                        optimisticData: (current) => ({
                            ratings: mergeRatings(current?.ratings, optimistic),
                        }),
                        rollbackOnError: true,
                        revalidate: false,
                        populateCache: true,
                    },
                );
                return;
            }

            await mutateFn();
        },
        [mutateSingle, mutateRatingsList, mergeRatings],
    );

    const revalidateCache = useCallback(async () => {
        if (mutateSingle) {
            await mutateSingle();
        } else if (mutateRatingsList) {
            await mutateRatingsList();
        }
    }, [mutateSingle, mutateRatingsList]);

    const handleLabelClick = useCallback(
        async (newLabel: MessageRatingLabel) => {
            // Per-handler re-entry guard: only bail when THIS handler is
            // already running. A concurrent comment-save (sibling handler)
            // is allowed to run in parallel; the backend upsert handles
            // both writes since label and comment are independent fields.
            if (labelChangeInFlightRef.current) {
                return;
            }
            labelChangeInFlightRef.current = true;

            const isToggleOff = selectedLabel === newLabel;

            if (isToggleOff) {
                setDraftComment('');
            }

            beginOperation();

            try {
                if (isToggleOff) {
                    await mutateRatingCache({
                        optimistic: null,
                        mutateFn: async () => {
                            if (isAdmin && messageId) {
                                await getApiClient().delete({
                                    url: '/admin/message-ratings/{message_id}/rating',
                                    pathParams: { message_id: messageId },
                                });
                            } else if (organizationId && stagedMessageId) {
                                await getApiClient().delete({
                                    url: '/organizations/{organization_id}/message-ratings/{staged_message_id}/rating',
                                    pathParams: {
                                        organization_id: organizationId,
                                        staged_message_id: stagedMessageId,
                                    },
                                });
                            }
                            return null;
                        },
                    });
                } else {
                    const optimisticRating = buildOptimisticRating(
                        newLabel,
                        draftComment || null,
                    );

                    await mutateRatingCache({
                        optimistic: optimisticRating,
                        mutateFn: async () => {
                            if (isAdmin && messageId) {
                                await getApiClient().put({
                                    url: '/admin/message-ratings/{message_id}/rating',
                                    pathParams: { message_id: messageId },
                                    body: {
                                        label: newLabel,
                                        comment: draftComment || null,
                                    } satisfies MessageRatingCreate,
                                });
                            } else if (organizationId && stagedMessageId) {
                                await getApiClient().put({
                                    url: '/organizations/{organization_id}/message-ratings/{staged_message_id}/rating',
                                    pathParams: {
                                        organization_id: organizationId,
                                        staged_message_id: stagedMessageId,
                                    },
                                    body: {
                                        label: newLabel,
                                        comment: draftComment || null,
                                    } satisfies MessageRatingCreate,
                                });
                            }
                            return optimisticRating;
                        },
                    });
                }
            } catch (error) {
                await revalidateCache();
            } finally {
                labelChangeInFlightRef.current = false;
                endOperation();
            }
        },
        [
            selectedLabel,
            draftComment,
            mutateRatingCache,
            messageId,
            organizationId,
            stagedMessageId,
            buildOptimisticRating,
            revalidateCache,
            beginOperation,
            endOperation,
            isAdmin,
        ],
    );

    const handleCommentChange = useCallback(
        (event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setDraftComment(event.target.value);
        },
        [],
    );

    const handleSaveComment = useCallback(async (): Promise<boolean> => {
        // Per-handler re-entry guard: bail if THIS handler is already
        // running (e.g. rapid double-blur). A concurrent label-change is
        // allowed and tracked separately via labelChangeInFlightRef.
        if (!selectedLabel || commentSaveInFlightRef.current) {
            return false;
        }
        commentSaveInFlightRef.current = true;
        beginOperation();

        const optimisticRating = buildOptimisticRating(
            selectedLabel,
            draftComment || null,
        );

        try {
            await mutateRatingCache({
                optimistic: optimisticRating,
                mutateFn: async () => {
                    if (isAdmin && messageId) {
                        await getApiClient().put({
                            url: '/admin/message-ratings/{message_id}/rating',
                            pathParams: { message_id: messageId },
                            body: {
                                comment: draftComment || null,
                            } satisfies MessageRatingCreate,
                        });
                    } else if (organizationId && stagedMessageId) {
                        await getApiClient().put({
                            url: '/organizations/{organization_id}/message-ratings/{staged_message_id}/rating',
                            pathParams: {
                                organization_id: organizationId,
                                staged_message_id: stagedMessageId,
                            },
                            body: {
                                comment: draftComment || null,
                            } satisfies MessageRatingCreate,
                        });
                    }
                    return optimisticRating;
                },
            });
            // After optimistic success, hasUnsavedComment will derive to false automatically
            return true;
        } catch (error) {
            await revalidateCache();
            return false;
        } finally {
            commentSaveInFlightRef.current = false;
            endOperation();
        }
    }, [
        selectedLabel,
        draftComment,
        mutateRatingCache,
        organizationId,
        stagedMessageId,
        messageId,
        buildOptimisticRating,
        revalidateCache,
        isAdmin,
        beginOperation,
        endOperation,
    ]);

    return {
        selectedLabel,
        isSaving,
        draftComment,
        setDraftComment,
        hasUnsavedComment,
        isOperationPending: isSaving,
        handleLabelClick,
        handleCommentChange,
        handleSaveComment,
    };
}
